import React, { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Sky, Environment } from '@react-three/drei'
import * as THREE from 'three'

// Lighting presets with realistic parameters
export interface LightingPreset {
  name: string
  description: string
  sunPosition: [number, number, number]
  sunIntensity: number
  sunColor: string
  ambientIntensity: number
  ambientColor: string
  skyTurbidity: number
  skyRayleigh: number
  skyMieCoefficient: number
  skyMieDirectionalG: number
  fogColor: string
  fogNear: number
  fogFar: number
  shadowMapSize: number
  shadowCameraNear: number
  shadowCameraFar: number
}

export const LIGHTING_PRESETS: Record<string, LightingPreset> = {
  morning: {
    name: 'Morning Light',
    description: 'Soft morning sunlight with long shadows',
    sunPosition: [50, 20, 50],
    sunIntensity: 2.5,
    sunColor: '#FFF4E6',
    ambientIntensity: 0.4,
    ambientColor: '#87CEEB',
    skyTurbidity: 2,
    skyRayleigh: 1,
    skyMieCoefficient: 0.005,
    skyMieDirectionalG: 0.8,
    fogColor: '#E6F3FF',
    fogNear: 50,
    fogFar: 200,
    shadowMapSize: 2048,
    shadowCameraNear: 0.1,
    shadowCameraFar: 100,
  },
  midday: {
    name: 'Midday Sun',
    description: 'Bright overhead sunlight with sharp shadows',
    sunPosition: [0, 100, 0],
    sunIntensity: 4.0,
    sunColor: '#FFFFFF',
    ambientIntensity: 0.6,
    ambientColor: '#B0E0E6',
    skyTurbidity: 1,
    skyRayleigh: 0.5,
    skyMieCoefficient: 0.005,
    skyMieDirectionalG: 0.7,
    fogColor: '#F0F8FF',
    fogNear: 100,
    fogFar: 300,
    shadowMapSize: 4096,
    shadowCameraNear: 0.1,
    shadowCameraFar: 150,
  },
  afternoon: {
    name: 'Afternoon Glow',
    description: 'Warm afternoon light with golden tones',
    sunPosition: [-50, 30, 50],
    sunIntensity: 3.0,
    sunColor: '#FFE4B5',
    ambientIntensity: 0.5,
    ambientColor: '#DDA0DD',
    skyTurbidity: 3,
    skyRayleigh: 2,
    skyMieCoefficient: 0.01,
    skyMieDirectionalG: 0.8,
    fogColor: '#FFE4E1',
    fogNear: 40,
    fogFar: 180,
    shadowMapSize: 2048,
    shadowCameraNear: 0.1,
    shadowCameraFar: 120,
  },
  sunset: {
    name: 'Golden Hour',
    description: 'Dramatic sunset lighting with warm colors',
    sunPosition: [-80, 10, 30],
    sunIntensity: 2.0,
    sunColor: '#FF6347',
    ambientIntensity: 0.3,
    ambientColor: '#FF69B4',
    skyTurbidity: 10,
    skyRayleigh: 3,
    skyMieCoefficient: 0.02,
    skyMieDirectionalG: 0.9,
    fogColor: '#FFB6C1',
    fogNear: 20,
    fogFar: 100,
    shadowMapSize: 2048,
    shadowCameraNear: 0.1,
    shadowCameraFar: 80,
  },
  overcast: {
    name: 'Overcast Sky',
    description: 'Soft diffused lighting with no harsh shadows',
    sunPosition: [0, 50, 0],
    sunIntensity: 1.5,
    sunColor: '#F5F5F5',
    ambientIntensity: 0.8,
    ambientColor: '#E0E0E0',
    skyTurbidity: 20,
    skyRayleigh: 0.1,
    skyMieCoefficient: 0.001,
    skyMieDirectionalG: 0.5,
    fogColor: '#D3D3D3',
    fogNear: 30,
    fogFar: 150,
    shadowMapSize: 1024,
    shadowCameraNear: 0.1,
    shadowCameraFar: 100,
  },
  night: {
    name: 'Night Scene',
    description: 'Moonlight with artificial lighting',
    sunPosition: [0, -50, 0],
    sunIntensity: 0.1,
    sunColor: '#4169E1',
    ambientIntensity: 0.1,
    ambientColor: '#191970',
    skyTurbidity: 1,
    skyRayleigh: 0.1,
    skyMieCoefficient: 0.001,
    skyMieDirectionalG: 0.5,
    fogColor: '#2F2F2F',
    fogNear: 10,
    fogFar: 80,
    shadowMapSize: 1024,
    shadowCameraNear: 0.1,
    shadowCameraFar: 60,
  },
}

interface EnhancedLightingSystemProps {
  preset: string
  timeOfDay?: number // 0-24 hours for dynamic lighting
  enableShadows?: boolean
  enableFog?: boolean
  enableHDRI?: boolean
}

export const EnhancedLightingSystem: React.FC<EnhancedLightingSystemProps> = ({
  preset = 'midday',
  timeOfDay,
  enableShadows = true,
  enableFog = true,
  enableHDRI = false,
}) => {
  const { scene, gl } = useThree()
  const directionalLightRef = useRef<THREE.DirectionalLight>(null)
  const ambientLightRef = useRef<THREE.AmbientLight>(null)
  
  const lightingConfig = LIGHTING_PRESETS[preset] || LIGHTING_PRESETS.midday

  // Configure shadow mapping
  useEffect(() => {
    if (enableShadows) {
      gl.shadowMap.enabled = true
      gl.shadowMap.type = THREE.PCFSoftShadowMap
      gl.shadowMap.autoUpdate = true
    }
  }, [gl, enableShadows])

  // Configure fog
  useEffect(() => {
    if (enableFog) {
      scene.fog = new THREE.Fog(
        lightingConfig.fogColor,
        lightingConfig.fogNear,
        lightingConfig.fogFar
      )
    } else {
      scene.fog = null
    }
    
    return () => {
      scene.fog = null
    }
  }, [scene, lightingConfig, enableFog])

  // Dynamic time-based lighting
  useFrame(() => {
    if (timeOfDay !== undefined && directionalLightRef.current) {
      // Calculate sun position based on time of day (0-24)
      const hour = timeOfDay % 24
      const sunAngle = ((hour - 6) / 12) * Math.PI // 6 AM to 6 PM = 0 to π
      
      const sunHeight = Math.sin(sunAngle) * 100
      const sunDistance = Math.cos(sunAngle) * 80
      
      directionalLightRef.current.position.set(sunDistance, Math.max(sunHeight, -20), 50)
      
      // Adjust intensity based on sun height
      const intensity = Math.max(0.1, Math.sin(sunAngle) * lightingConfig.sunIntensity)
      directionalLightRef.current.intensity = intensity
      
      // Color temperature changes throughout the day
      let colorTemp = '#FFFFFF'
      if (hour < 8 || hour > 18) {
        colorTemp = '#FFE4B5' // Warm morning/evening
      } else if (hour < 10 || hour > 16) {
        colorTemp = '#FFF8DC' // Slightly warm
      }
      
      directionalLightRef.current.color.setStyle(colorTemp)
    }
  })

  return (
    <>
      {/* Sky and Environment */}
      {enableHDRI ? (
        <Environment preset="sunset" background />
      ) : (
        <Sky
          distance={450000}
          sunPosition={lightingConfig.sunPosition}
          inclination={0}
          azimuth={0.25}
          turbidity={lightingConfig.skyTurbidity}
          rayleigh={lightingConfig.skyRayleigh}
          mieCoefficient={lightingConfig.skyMieCoefficient}
          mieDirectionalG={lightingConfig.skyMieDirectionalG}
        />
      )}

      {/* Main Directional Light (Sun) */}
      <directionalLight
        ref={directionalLightRef}
        position={lightingConfig.sunPosition}
        intensity={lightingConfig.sunIntensity}
        color={lightingConfig.sunColor}
        castShadow={enableShadows}
        shadow-mapSize-width={lightingConfig.shadowMapSize}
        shadow-mapSize-height={lightingConfig.shadowMapSize}
        shadow-camera-near={lightingConfig.shadowCameraNear}
        shadow-camera-far={lightingConfig.shadowCameraFar}
        shadow-camera-left={-50}
        shadow-camera-right={50}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
        shadow-bias={-0.0001}
      />

      {/* Ambient Light */}
      <ambientLight
        ref={ambientLightRef}
        intensity={lightingConfig.ambientIntensity}
        color={lightingConfig.ambientColor}
      />

      {/* Fill Light (subtle) */}
      <directionalLight
        position={[-lightingConfig.sunPosition[0], lightingConfig.sunPosition[1] * 0.3, -lightingConfig.sunPosition[2]]}
        intensity={lightingConfig.sunIntensity * 0.2}
        color={lightingConfig.ambientColor}
        castShadow={false}
      />

      {/* Ground Bounce Light (simulates light reflecting off ground) */}
      <directionalLight
        position={[0, -10, 0]}
        intensity={lightingConfig.ambientIntensity * 0.5}
        color="#F5F5DC"
        castShadow={false}
      />
    </>
  )
}

// Hook for getting current lighting info
export const useLightingInfo = (preset: string) => {
  const config = LIGHTING_PRESETS[preset] || LIGHTING_PRESETS.midday
  
  return {
    config,
    isNight: preset === 'night',
    isDaytime: ['morning', 'midday', 'afternoon'].includes(preset),
    isGoldenHour: ['morning', 'sunset'].includes(preset),
    shadowQuality: config.shadowMapSize >= 2048 ? 'high' : 'medium',
  }
}

// Time-based preset selector
export const getPresetForTime = (hour: number): string => {
  if (hour >= 6 && hour < 10) return 'morning'
  if (hour >= 10 && hour < 16) return 'midday'
  if (hour >= 16 && hour < 19) return 'afternoon'
  if (hour >= 19 && hour < 21) return 'sunset'
  return 'night'
}

export default EnhancedLightingSystem
